import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { format, subMonths, subYears } from 'date-fns';
import type { AccountBalanceDetail } from '@/models/account/types';
import { fetchAccountBalanceDetail } from '@/models/account/service';

export type DateFilter = '1month' | '3month' | '6month' | 'year';

function getDateRange(filter: DateFilter): { dateStart: string; dateEnd: string } {
  const end = new Date();
  const monthsMap: Record<DateFilter, number> = { '1month': 1, '3month': 3, '6month': 6, year: 0 };
  const start = filter === 'year' ? subYears(end, 1) : subMonths(end, monthsMap[filter]);
  return {
    dateStart: format(start, 'yyyy-MM-dd'),
    dateEnd: format(end, 'yyyy-MM-dd'),
  };
}

export function useBalanceDetail(idAccount: string | undefined) {
  const store = useLocalObservable(() => ({
    items: [] as AccountBalanceDetail[],
    isLoading: false,
    error: null as string | null,
    dateFilter: '1month' as DateFilter,
    expandedRows: [] as string[],
  }));

  function reload(filter?: DateFilter) {
    if (!idAccount) return;
    const { dateStart, dateEnd } = getDateRange(filter ?? store.dateFilter);
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    fetchAccountBalanceDetail(idAccount, dateStart, dateEnd)
      .then((details) =>
        runInAction(() => {
          store.items = details
            .filter((d) => d.date >= dateStart && d.date <= dateEnd)
            .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          store.isLoading = false;
        })
      )
      .catch((err: Error) =>
        runInAction(() => {
          store.error = err.message;
          store.isLoading = false;
        })
      );
  }

  function setDateFilter(value: DateFilter) {
    runInAction(() => {
      store.dateFilter = value;
    });
    reload(value);
  }

  function toggleExpanded(rowKey: string) {
    runInAction(() => {
      const i = store.expandedRows.indexOf(rowKey);
      if (i >= 0) store.expandedRows.splice(i, 1);
      else store.expandedRows.push(rowKey);
    });
  }

  return {
    get items() {
      return store.items;
    },
    get isLoading() {
      return store.isLoading;
    },
    get error() {
      return store.error;
    },
    get dateFilter() {
      return store.dateFilter;
    },
    get expandedRows() {
      return store.expandedRows;
    },
    reload,
    setDateFilter,
    toggleExpanded,
  };
}
